// Channels page — 6 channel cards (WhatsApp · SMS · Web Chat · Telegram · LINE · Email).
// Only WhatsApp has setup wired in Phase 5; the rest land in later phases.

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Send,
  Smartphone,
  type LucideIcon,
} from 'lucide-react';
import type { ChannelListItem } from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Badge } from '@/components/primitives/Badge';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Btn } from '@/components/primitives/Btn';
import { PageContainer, PageHeader } from '@/components/PageHeader';
import { listChannels } from '@/lib/channels-api';

const ICON_BY_TYPE: Record<string, LucideIcon> = {
  WHATSAPP: MessageCircle,
  SMS: Smartphone,
  WEBCHAT: MessageSquare,
  TELEGRAM: Send,
  LINE: Phone,
  EMAIL: Mail,
};

const PROVIDER_LABEL: Record<string, string> = {
  BAILEYS: 'Baileys',
  INFOBIP: 'Infobip',
  BIRD: 'Bird',
  META_CLOUD: 'Meta Cloud API',
  TWILIO: 'Twilio',
  CUSTOM: 'Custom',
};

const FRIENDLY_TYPE: Record<string, string> = {
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  WEBCHAT: 'Web Chat',
  TELEGRAM: 'Telegram',
  LINE: 'LINE',
  EMAIL: 'Email',
};

export function ChannelsPage() {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['channels'], queryFn: listChannels });

  return (
    <PageContainer>
      <PageHeader
        title="Channels"
        description="Connect Qualibot agents to the channels your users live on"
      />

      {query.isLoading ? (
        <div className="text-[13px] text-text-3 py-10 text-center">Loading channels…</div>
      ) : (
        <div className="grid grid-cols-3 gap-3.5">
          {query.data?.map((c) => (
            <ChannelCard key={c.id} c={c} onOpen={() => navigate('/channels/whatsapp')} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

function ChannelCard({ c, onOpen }: { c: ChannelListItem; onOpen: () => void }) {
  const Icon = ICON_BY_TYPE[c.type] ?? MessageSquare;
  const isWhatsApp = c.type === 'WHATSAPP';
  const isConnected = c.status === 'Connected';
  const isAvailable = c.status === 'Available';

  return (
    <Card pad={0}>
      <div className="p-[18px]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{
                background: isConnected ? 'var(--accent-soft)' : 'var(--surface-2)',
                color: isConnected ? 'var(--accent-strong)' : 'var(--text-2)',
                border: isConnected ? 'none' : '1px solid var(--border)',
              }}
            >
              <Icon size={18} strokeWidth={1.7} />
            </span>
            <div>
              <div className="font-semibold text-[14.5px]">
                {FRIENDLY_TYPE[c.type] ?? c.displayName}
              </div>
              <div className="text-[11.5px] text-text-3 mt-0.5">
                {PROVIDER_LABEL[c.provider] ?? c.provider}
              </div>
            </div>
          </div>
          <StatusBadge status={c.status} />
        </div>

        <div className="text-[13px] text-text-2 min-h-[34px]">
          {c.externalIdentifier ?? (isAvailable ? 'Available to connect' : 'Not configured yet')}
        </div>

        <div className="flex items-center flex-wrap gap-1.5 mt-2.5">
          {c.sessionCount !== undefined && c.sessionCount > 0 && (
            <Badge>
              {c.sessionCount} session{c.sessionCount === 1 ? '' : 's'}
            </Badge>
          )}
          {c.boundAgentNames.map((a) => (
            <Badge key={a} tone="accent">
              {a}
            </Badge>
          ))}
        </div>
      </div>

      <div className="border-t border-border px-[18px] py-2.5 flex items-center justify-between">
        <span className="text-[12px] text-text-3">
          {c.boundAgentNames.length > 0
            ? `${c.boundAgentNames.length} bound agent${c.boundAgentNames.length === 1 ? '' : 's'}`
            : 'No agent bound'}
        </span>
        {isWhatsApp ? (
          <Btn size="sm" onClick={onOpen}>
            Configure
          </Btn>
        ) : (
          <Btn size="sm" disabled>
            {isAvailable ? 'Soon' : 'Soon'}
          </Btn>
        )}
      </div>
    </Card>
  );
}
